export interface IMutationError {
    code: number;
    message: string;
}

const MutationError =
    <Context extends object>(code: number, message: string) =>
    (detail: Context) => {
        return new Error(`[${code}] ${message}: ${JSON.stringify(detail)}`);
    };

// Shape Errors: 1000~1099
export const ShapeNotFoundError = MutationError(1000, 'shape is not found');
export const ShapeNotInPageError = MutationError(1001, 'shape is not in page');
export const ShapeNotChildOfShapeTreeRootError = MutationError(1002, 'shape is not a child of shape tree root');
export const ShapeTypeNotMatchError = MutationError(1003, 'shape type not match');
export const ShapeInsertPositionOutOfRangeError = MutationError(1004, 'shape insert index out of range');
export const ShapeNoteFoundExist = MutationError(1005, 'shape note already exist');
export const ShapeNotInPositionError = MutationError(1006, 'shape is not in position');
export const ShapeNotChildOfGroupShapeError = MutationError(1007, 'shape is not a child of group shape');
export const ShapePlaceholderNotExistError = MutationError(1008, 'shape placeholder not exist');
export const ShapeInvalidTypeError = MutationError(1009, 'shape type invalid');
export const ShapeListLengthTooShortError = MutationError(1011, 'shape list length too short');
export const ShapeRecursiveInsertionError = MutationError(1012, 'group shape can not insert to itself');

export enum TableErrorCode {
    TableAddRowOrColCountError = 1020,
    TableAddRowOrColIndexOutOfRangeError = 1021,
    TableDeleteRowOrColCountOutOfLengthError = 1022,
    TableDeleteRowOrColIndexOutOfRangeError = 1023,
    TableRangeExistNullError = 1024,
    TableRangeLessZeroError = 1025,
    TableRangeOutOfLengthError = 1026,
    TableMergeAlreadyMergedCellError = 1027,
    TableUnMergeMultipleCellError = 1028,
    TableInsertRowInMergedCellError = 1029,
    TableInsertColInMergedCellError = 1030,
    TableDeleteRowInMergedCellError = 1031,
    TableDeleteColInMergedCellError = 1031,
    TableDeleteAllColOrRowError = 1032,
    TableMergeSingleCellError = 1033,
}

export const TableAddRowOrColCountError = MutationError(
    TableErrorCode.TableAddRowOrColCountError,
    'table add row or col count error',
);
export const TableAddRowOrColIndexOutOfRangeError = MutationError(
    TableErrorCode.TableAddRowOrColIndexOutOfRangeError,
    'table add row or col index out of range',
);
export const TableDeleteRowOrColCountOutOfLengthError = MutationError(
    TableErrorCode.TableDeleteRowOrColCountOutOfLengthError,
    'table delete row or col out of length error',
);
export const TableDeleteRowOrColIndexOutOfRangeError = MutationError(
    TableErrorCode.TableDeleteRowOrColIndexOutOfRangeError,
    'table delete row or col index out of range',
);
export const TableRangeExistNullError = MutationError(
    TableErrorCode.TableRangeExistNullError,
    'table range exist null value',
);
export const TableRangeLessZeroError = MutationError(TableErrorCode.TableRangeLessZeroError, 'table range less zero');
export const TableRangeOutOfLengthError = MutationError(
    TableErrorCode.TableRangeOutOfLengthError,
    'table range out of length',
);
export const TableMergeAlreadyMergedCellError = MutationError(
    TableErrorCode.TableMergeAlreadyMergedCellError,
    'table merge already merged cells',
);
export const TableUnMergeMultipleCellError = MutationError(
    TableErrorCode.TableUnMergeMultipleCellError,
    'table unMerge multiple cells',
);
export const TableInsertRowInMergedCellError = MutationError(
    TableErrorCode.TableInsertRowInMergedCellError,
    'table insert row in merged cell',
);
export const TableInsertColInMergedCellError = MutationError(
    TableErrorCode.TableInsertColInMergedCellError,
    'table insert col in merged cell',
);
export const TableDeleteRowInMergedCellError = MutationError(
    TableErrorCode.TableDeleteRowInMergedCellError,
    'table delete row in merged cell',
);
export const TableDeleteColInMergedCellError = MutationError(
    TableErrorCode.TableDeleteColInMergedCellError,
    'table delete col in merged cell',
);
export const TableDeleteAllColOrRowError = MutationError(
    TableErrorCode.TableDeleteAllColOrRowError,
    'table delete all col or row',
);
export const TableMergeSingleCellError = MutationError(
    TableErrorCode.TableMergeSingleCellError,
    'table merge single cell',
);

// Page Errors: 1100~1199
export const PageNotFoundError = MutationError(1100, 'page is not found');
export const LayoutNotBelongToMasterError = MutationError(1101, 'layout is not belong to master');
export const PagePositionOutOfRangeError = MutationError(1102, 'page index out of range');
export const PageInsertPositionOutOfRangeError = MutationError(1103, 'page insert index out of range');
export const PageNotInPositionError = MutationError(1104, 'page is not in position');
export const PageShapeTreeNotExistError = MutationError(1105, 'page shape tree not exist');
export const PageStaleReferenceError = MutationError(1106, 'page stale reference');

// Animation Errors: 1200~1299
export const TimingSeqNotFoundError = MutationError(1200, 'timing sequence is not found');
export const UnitTimeNodePositionOutOfRangeError = MutationError(1201, 'timing unit time node out of range');
export const UnitTimeNodeInsertPositionOutOfRangeError = MutationError(
    1202,
    'timing unit time node insert index out of range',
);

// Section Errors: 1300~1399
export const SectionNotFoundError = MutationError(1300, 'section is not found');
export const SectionBindingSlideIndexOutOfRangeError = MutationError(1301, 'section binding slide index out of range');
export const SectionInsertPositionOutOfRangeError = MutationError(1302, 'section insert index out of range');

// Theme Errors: 1400~1499
export const ThemeHasExistError = MutationError(1400, 'theme has exist in presentation');
export const ThemeUsedError = MutationError(1401, 'theme is used by master');
export const ThemeNotExistError = MutationError(1403, 'theme not exist in presentation');
export const ThemeFormatError = MutationError(1404, 'theme has wrong format');

// Text Errors: 1500~1599s
export const TextPropertyTypeError = MutationError(1500, 'text property type error');
export const TextContentFieldError = MutationError(1501, 'text content field error');

// Chart Errors: 1900~1999
export const ChartNotFoundError = MutationError(1901, 'chart is not found');
export const SubChartPositionOutOfRangeError = MutationError(1902, 'sub chart index out of range');
export const SubChartNotFoundError = MutationError(1903, 'sub chart is not found');
export const SubChartTypeNotMatchError = MutationError(1904, 'sub chart type not match');

// Common Errors: 2000~2999
export const ModelAlreadyExistError = MutationError(2000, 'model already exist');
export const ModelNotFoundError = MutationError(2001, 'model not found');

// ResourceMap Errors: 3000~3999
export const ResourceMapIdIsEmptyError = MutationError(3000, 'resource map id is empty');

// comment Errors: 4000~4099
export const CommentGroupIsEmptyError = MutationError(4000, 'commentGroup is Empty');
export const CommentGroupShapeIdIsEmptyError = MutationError(4001, 'shapeId is empty in element or text comment');
export const CommentGroupCommentTypeIsEmptyError = MutationError(4002, 'commentType is empty in commentGroup');
export const CommentGroupIdIsEmptyError = MutationError(4003, 'groupId is empty in comment');
export const CommentGroupIdIsRepeatedError = MutationError(4004, 'groupId is repeated in comment');
export const CommentGroupAnchorPositionIsEmptyError = MutationError(4005, 'anchorPosition is empty in text comment');
export const CommentGroupAnchorPositionIsInvalidError = MutationError(
    4006,
    'anchorPosition.x or anchorPosition.y is invalid in commentGroup',
);
export const CommentGroupTextAnchorIsEmptyError = MutationError(4007, 'textAnchor is empty in text comment');

export const CommentGroupCpOfTextAnchorIsInvalidError = MutationError(
    4008,
    'textAnchor.cp should equal or larger than 0',
);
export const CommentGroupLenOfTextAnchorIsInvalidError = MutationError(
    4009,
    'textAnchor.len should equal or larger than 0',
);
export const CommentGroupTableRangeStartIndexIsInvalidError = MutationError(
    4010,
    'tableRange.startColIndex or tableRange.startRowIndex should equal or larger than 0',
);
export const CommentGroupTableRangeSpanIsInvalidError = MutationError(
    4011,
    'tableRange.colSpan or tableRange.rowSpan should equal or larger than 0',
);

// dataExtension Errors: 5000~5999
export const ExtensionHasPageIdError = MutationError(5000, 'extensions mutation should has pageId');
export const ExtensionHasTargetIdError = MutationError(5001, 'extensions mutation should has targetId');
export const ExtensionNoPageIdError = MutationError(5002, 'extensions mutation should not has pageId');
export const ExtensionNoTargetIdError = MutationError(5003, 'extensions mutation should not has targetId');
export const ExtensionDiffIdError = MutationError(5004, 'extensions mutation should not has diff targetId and pageId');
